import secureLocalStorage from "react-secure-storage";

const TOKEN_KEY = 'auth_token';

/**
 * Store JWT token in secure storage
 */
export function setAuthToken(token: string): void {
    secureLocalStorage.setItem(TOKEN_KEY, token);
}

/**
 * Get JWT token from secure storage
 */
export function getAuthToken(): string | null {
    const token = secureLocalStorage.getItem(TOKEN_KEY);
    return typeof token === 'string' ? token : null;
}

/**
 * Remove JWT token from storage
 */
export function removeAuthToken(): void {
    secureLocalStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if user has a valid token stored
 */
export function hasAuthToken(): boolean {
    return getAuthToken() !== null;
}

/**
 * Decode JWT payload (middle segment) without verifying the signature.
 * Used only for client-side hints (expiry, guest vs user); authorization remains server-side.
 */
export function parseJwtPayload(token: string): Record<string, unknown> | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3 || !parts[1]) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const json = atob(padded);
        return JSON.parse(json) as Record<string, unknown>;
    } catch {
        return null;
    }
}

/**
 * @returns exp claim in milliseconds since epoch, or null if missing/unparseable
 */
export function getJwtExpiryMs(token: string): number | null {
    const payload = parseJwtPayload(token);
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
}

/**
 * Whether the JWT is past its exp (with optional clock skew).
 * If exp is missing, treats as expired so the app falls back to refresh/create flows.
 */
export function isJwtExpired(token: string, skewSeconds = 60): boolean {
    const expMs = getJwtExpiryMs(token);
    if (expMs === null) return true;
    return Date.now() >= expMs - skewSeconds * 1000;
}

/** True if decoded payload looks like a guest token (matches backend guest JWT shape). */
export function isGuestJwtToken(token: string): boolean {
    const p = parseJwtPayload(token);
    return p !== null && p.isGuest === true && typeof p.guestId === 'string';
}
